<script setup lang="ts">
import { AdSlot, AppShell } from '@it-tools/shell'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BuildForm from './components/BuildForm.vue'
import EnergyPanel from './components/EnergyPanel.vue'
import ResultsPanel from './components/ResultsPanel.vue'
import UsageForm from './components/UsageForm.vue'
import { compareTiers, estimateEnergy } from './model/energy'
import { estimateLoad, recommendPsu } from './model/power'
import type { Spec } from './model/spec'
import { DEFAULT_SPEC, isUsableSpec, readLocationSpec, replaceLocationSpec } from './model/urlState'

const { t } = useI18n()

const spec = ref<Spec>(DEFAULT_SPEC)
const shareNotice = ref(false)

const load = computed(() => estimateLoad(spec.value.build))
const psu = computed(() => (load.value ? recommendPsu(load.value.peakWatts) : null))
const energy = computed(() =>
  load.value && psu.value ? estimateEnergy(load.value, psu.value.watts, spec.value.usage) : null,
)
const tiers = computed(() =>
  load.value && psu.value ? compareTiers(load.value, psu.value.watts, spec.value.usage) : [],
)

// A shared link restores the build; a malformed one silently falls back to the
// default so the visitor always lands on a working calculator.
onMounted(() => {
  const restored = readLocationSpec()
  if (restored) spec.value = restored
})

// Only a calculable build goes in the URL — a half-typed wattage would produce
// links that restore nothing.
watch(spec, (value) => {
  if (isUsableSpec(value)) replaceLocationSpec(value)
})

async function share(): Promise<void> {
  if (isUsableSpec(spec.value)) replaceLocationSpec(spec.value)
  try {
    await navigator.clipboard.writeText(window.location.href)
    shareNotice.value = true
    setTimeout(() => (shareNotice.value = false), 2000)
  } catch {
    // Clipboard access can be denied; the URL bar already holds the link.
  }
}
</script>

<template>
  <AppShell tool-name-key="tools.psuCalculator">
    <template #controls>
      <span v-if="shareNotice" class="notice" role="status">{{ t('common.copied') }}</span>
    </template>

    <div class="layout">
      <div class="layout__main">
        <div class="calculator">
          <div class="calculator__inputs">
            <BuildForm
              class="calculator__build"
              :model-value="spec.build"
              @update:model-value="(build) => (spec = { ...spec, build })"
            />
            <UsageForm
              class="calculator__usage"
              :model-value="spec.usage"
              @update:model-value="(usage) => (spec = { ...spec, usage })"
            />
            <div class="calculator__actions">
              <button type="button" @click="spec = DEFAULT_SPEC">{{ t('common.reset') }}</button>
              <button type="button" :disabled="!isUsableSpec(spec)" @click="share()">
                {{ t('common.share') }}
              </button>
            </div>
          </div>

          <div class="calculator__results">
            <ResultsPanel class="calculator__psu" :load="load" :psu="psu" />
            <EnergyPanel
              class="calculator__energy"
              :energy="energy"
              :tiers="tiers"
              :tier="spec.usage.efficiency"
            />
          </div>
        </div>

        <p class="hint">{{ t('hint.usage') }}</p>

        <AdSlot placement="leaderboard" />
      </div>

      <AdSlot placement="sidebar" />
    </div>
  </AppShell>
</template>

<style scoped>
.layout {
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
}

.layout__main {
  flex: 1;
  min-width: 0;
}

.calculator {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(18rem, 26rem);
  gap: var(--space-4) var(--space-6);
  align-items: start;
}

.calculator__inputs,
.calculator__results {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

/* The answer stays in view while the form scrolls past. */
.calculator__results {
  position: sticky;
  top: var(--space-4);
}

/*
 * One column on narrow screens, each answer straight after the inputs it depends
 * on: components, then the power supply; usage, then the energy cost.
 */
@media (max-width: 56rem) {
  .calculator {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .calculator__inputs,
  .calculator__results {
    display: contents;
  }

  .calculator__build {
    order: 1;
  }

  .calculator__psu {
    order: 2;
  }

  .calculator__usage {
    order: 3;
  }

  .calculator__energy {
    order: 4;
  }

  .calculator__actions {
    order: 5;
  }
}

.calculator__actions {
  display: flex;
  gap: var(--space-2);
}

button {
  padding: var(--space-2) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

button:hover:not(:disabled) {
  background: var(--color-surface-raised);
  border-color: var(--color-border-strong);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hint {
  margin-top: var(--space-4);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.notice {
  font-size: var(--text-sm);
  color: var(--color-success);
}
</style>
